import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import type { NotificationStyle } from '../types';

let localNotificationsModule: typeof import('@capacitor/local-notifications') | null = null;
let channelReady = false;
let reminderChannelsReady = false;
let actionTypesReady = false;

const TIMER_ACTION_TYPE_ID = 'timer_actions';
const TIMER_ACTION_DONE_ID = 'timer_done';
const TIMER_ACTION_SNOOZE_ID = 'timer_snooze_2m';
const REMINDER_CHANNEL_QUIET = 'reminders_quiet';
const REMINDER_CHANNEL_STANDARD = 'reminders_standard';

type TimerNotificationAction = 'done' | 'snooze';

async function getLocalNotifications(): Promise<typeof import('@capacitor/local-notifications') | null> {
  if (!Capacitor.isNativePlatform()) return null;
  if (localNotificationsModule) return localNotificationsModule;
  try {
    localNotificationsModule = await import('@capacitor/local-notifications');
    return localNotificationsModule;
  } catch {
    return null;
  }
}

export async function requestTimerNotificationsPermission(): Promise<void> {
  await ensureNotificationsPermission();
  if (Capacitor.isNativePlatform()) {
    await ensureTimerActionTypes();
    await ensureTimerNotificationChannel();
  }
}

export async function requestReminderNotificationsPermission(): Promise<void> {
  await ensureNotificationsPermission();
  if (Capacitor.isNativePlatform()) {
    await ensureReminderNotificationChannels();
  }
}

export async function sendTimerDoneNotification(message: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const mod = await getLocalNotifications();
    if (!mod) return;
    await ensureNotificationsPermission();
    await ensureTimerNotificationChannel();
    await ensureTimerActionTypes();
    const perms = await mod.LocalNotifications.checkPermissions();
    if (perms.display !== 'granted') return;
    const now = Date.now();
    await mod.LocalNotifications.schedule({
      notifications: [
        {
          id: Number(String(now).slice(-9)),
          title: 'Rethread',
          body: message,
          channelId: 'timers',
          actionTypeId: TIMER_ACTION_TYPE_ID
        }
      ]
    });
    return;
  }

  if ('Notification' in window && Notification.permission === 'granted') {
    // eslint-disable-next-line no-new
    new Notification(message);
  }
}

export async function scheduleTimerDoneNotification(
  notificationId: number,
  atMs: number,
  message: string
): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  const mod = await getLocalNotifications();
  if (!mod) return false;
  await ensureNotificationsPermission();
  await ensureTimerNotificationChannel();
  await ensureTimerActionTypes();
  const perms = await mod.LocalNotifications.checkPermissions();
  if (perms.display !== 'granted') return false;
  await mod.LocalNotifications.schedule({
    notifications: [
      {
        id: notificationId,
        title: 'Rethread',
        body: message,
        channelId: 'timers',
        actionTypeId: TIMER_ACTION_TYPE_ID,
        schedule: { at: new Date(atMs), allowWhileIdle: true }
      }
    ]
  });
  return true;
}

export async function cancelTimerDoneNotification(notificationId: number | null): Promise<void> {
  if (!Capacitor.isNativePlatform() || notificationId === null) return;
  const mod = await getLocalNotifications();
  if (!mod) return;
  await mod.LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
}

export async function scheduleReminderNotification(
  notificationId: number,
  atMs: number,
  title: string,
  message: string,
  style: Exclude<NotificationStyle, 'off'>
): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  const mod = await getLocalNotifications();
  if (!mod) return false;
  await ensureNotificationsPermission();
  await ensureReminderNotificationChannels();
  const perms = await mod.LocalNotifications.checkPermissions();
  if (perms.display !== 'granted') return false;
  await mod.LocalNotifications.schedule({
    notifications: [
      {
        id: notificationId,
        title,
        body: message,
        channelId: style === 'quiet' ? REMINDER_CHANNEL_QUIET : REMINDER_CHANNEL_STANDARD,
        schedule: { at: new Date(atMs), allowWhileIdle: true }
      }
    ]
  });
  return true;
}

export async function cancelNotification(notificationId: number | null): Promise<void> {
  if (!Capacitor.isNativePlatform() || notificationId === null) return;
  const mod = await getLocalNotifications();
  if (!mod) return;
  await mod.LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
}

export async function checkExactAlarmPermission(): Promise<'granted' | 'denied' | 'unknown'> {
  if (!Capacitor.isNativePlatform()) return 'unknown';
  const mod = await getLocalNotifications();
  if (!mod) return 'unknown';
  try {
    const status = await mod.LocalNotifications.checkExactNotificationSetting();
    return status.exact_alarm === 'granted' ? 'granted' : status.exact_alarm === 'denied' ? 'denied' : 'unknown';
  } catch {
    return 'unknown';
  }
}

export async function openExactAlarmSettings(): Promise<'granted' | 'denied' | 'unknown'> {
  if (!Capacitor.isNativePlatform()) return 'unknown';
  const mod = await getLocalNotifications();
  if (!mod) return 'unknown';
  try {
    const status = await mod.LocalNotifications.changeExactNotificationSetting();
    return status.exact_alarm === 'granted' ? 'granted' : status.exact_alarm === 'denied' ? 'denied' : 'unknown';
  } catch {
    return 'unknown';
  }
}

export async function listenForTimerNotificationActions(onAction: (action: TimerNotificationAction) => void): Promise<(() => void) | null> {
  if (!Capacitor.isNativePlatform()) return null;
  const mod = await getLocalNotifications();
  if (!mod) return null;
  await ensureTimerActionTypes();
  let handle: PluginListenerHandle | null = null;
  try {
    handle = await mod.LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
      if (event.actionId === TIMER_ACTION_DONE_ID || event.actionId === 'tap') {
        onAction('done');
        return;
      }
      if (event.actionId === TIMER_ACTION_SNOOZE_ID) {
        onAction('snooze');
      }
    });
  } catch {
    return null;
  }
  return () => {
    if (handle) {
      void handle.remove();
    }
  };
}

async function ensureTimerNotificationChannel(): Promise<void> {
  if (!Capacitor.isNativePlatform() || channelReady) return;
  const mod = await getLocalNotifications();
  if (!mod) return;
  try {
    await mod.LocalNotifications.createChannel({
      id: 'timers',
      name: 'Timer Alerts',
      description: 'Rescue timer completion alerts',
      importance: 5,
      visibility: 1
    });
  } catch {
    // channel may already exist
  }
  channelReady = true;
}

async function ensureReminderNotificationChannels(): Promise<void> {
  if (!Capacitor.isNativePlatform() || reminderChannelsReady) return;
  const mod = await getLocalNotifications();
  if (!mod) return;
  try {
    await mod.LocalNotifications.createChannel({
      id: REMINDER_CHANNEL_QUIET,
      name: 'Gentle Reminders',
      description: 'Low-key reminders to re-enter gently',
      importance: 2,
      visibility: 1
    });
  } catch {
    // channel may already exist
  }
  try {
    await mod.LocalNotifications.createChannel({
      id: REMINDER_CHANNEL_STANDARD,
      name: 'Reminders',
      description: 'Standard reminders to re-enter and reflect',
      importance: 3,
      visibility: 1
    });
  } catch {
    // channel may already exist
  }
  reminderChannelsReady = true;
}

async function ensureTimerActionTypes(): Promise<void> {
  if (!Capacitor.isNativePlatform() || actionTypesReady) return;
  const mod = await getLocalNotifications();
  if (!mod) return;
  try {
    await mod.LocalNotifications.registerActionTypes({
      types: [
        {
          id: TIMER_ACTION_TYPE_ID,
          actions: [
            { id: TIMER_ACTION_DONE_ID, title: 'Done' },
            { id: TIMER_ACTION_SNOOZE_ID, title: 'Snooze 2m' }
          ]
        }
      ]
    });
  } catch {
    // no-op
  }
  actionTypesReady = true;
}

async function ensureNotificationsPermission(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const mod = await getLocalNotifications();
    if (!mod) return;
    const perms = await mod.LocalNotifications.checkPermissions();
    if (perms.display !== 'granted') {
      await mod.LocalNotifications.requestPermissions();
    }
    return;
  }

  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
}
